import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Block as AvoidIcon,
  Repeat as CadenceIcon,
  Lightbulb as WhyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { visiblePlatforms, getPlatformColor, getPlatformName } from '../config/platforms';
import {
  WEEKDAYS,
  SCHEDULE,
  GUIDANCE,
  SPACING_GUIDANCE,
  SCHEDULE_CHANGELOG,
  formatTime,
  slotsFor,
} from '../config/postingSchedule';

/**
 * The team's weekly posting rhythm, and per-network guidance on timing.
 *
 * Two halves: the matrix is what to post when, at a glance; the cards below are the reasoning
 * and the format notes, which is what makes the matrix editable by someone who was not in the
 * room when it was written. The previous schedule was a spreadsheet with times and no
 * rationale, so it went three years without anyone being able to tell which parts still held.
 */
export const PostingSchedule: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>('linkedin');

  // Coming-soon platforms are shown but cannot be scheduled, matching the rest of the app
  const platforms = visiblePlatforms();
  const scheduled = platforms.filter(p => p.status === 'available');

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Posting Schedule
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The weekly rhythm, and when each network's audience is actually paying attention.
      </Typography>

      {/* The honesty this page needs: these are starting points, not measurements. Saying so
          is what stops it calcifying the way the last schedule did. */}
      <Alert severity="info" icon={<WhyIcon />} sx={{ mb: 3 }}>
        <AlertTitle>Starting points, not measured facts</AlertTitle>
        These are the times the team has been posting at, from general recommendations about how
        each network is used — not from an analysis of this team's own results. Keep to them for a
        month, then check{' '}
        <Link component="button" onClick={() => navigate('/analytics')} sx={{ verticalAlign: 'baseline' }}>
          Stats
        </Link>{' '}
        and move whatever your own audience disagrees with.
        <Box component="span" sx={{ display: 'block', mt: 1, fontSize: '0.85rem', opacity: 0.9 }}>
          Revised {SCHEDULE_CHANGELOG.revised}. Previously: {SCHEDULE_CHANGELOG.previous}.
        </Box>
        {/* Named on the page, not just in the file. The previous schedule went three years
            unquestioned because it recorded times and no source — someone reading this should
            be able to see where the numbers came from and judge them. */}
        <Box component="span" sx={{ display: 'block', mt: 1, fontSize: '0.85rem', opacity: 0.9 }}>
          <strong>Where these come from.</strong> {SCHEDULE_CHANGELOG.professionalSource}, for
          every network. Instagram: {SCHEDULE_CHANGELOG.instagramSource}.
          None of it is a measurement of your own audience — Stats is, once there is enough of it.
        </Box>
      </Alert>

      {/* ---------------- The matrix ---------------- */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Weekly schedule</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            All times US Eastern. Hover a slot for the reasoning.
          </Typography>

          {/* Scrolls inside its own container: seven day columns do not fit a phone, and the
              page body must never scroll sideways. */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `minmax(130px, 1.2fr) repeat(7, minmax(96px, 1fr))`,
                gap: 1,
                minWidth: 780,
              }}
            >
              {/* Header row */}
              <Box />
              {WEEKDAYS.map(day => (
                <Box
                  key={day.key}
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    pb: 0.5,
                  }}
                >
                  {day.short}
                </Box>
              ))}

              {scheduled.map(platform => (
                <React.Fragment key={platform.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: getPlatformColor(platform.id),
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {platform.name}
                    </Typography>
                  </Box>

                  {WEEKDAYS.map(day => {
                    const slot = SCHEDULE[platform.id]?.[day.key];

                    if (!slot) {
                      return (
                        <Box
                          key={day.key}
                          sx={{
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            minHeight: 62,
                            opacity: 0.4,
                          }}
                        />
                      );
                    }

                    return (
                      <Tooltip
                        key={day.key}
                        arrow
                        title={
                          slot.also
                            ? `${slot.theme} at ${formatTime(slot.time)}, and ${slot.also.theme} at ${formatTime(slot.also.time)}`
                            : slot.note || `${slot.theme} — ${formatTime(slot.time)}`
                        }
                      >
                        <Box
                          sx={{
                            // Occasional slots are dashed: a standing reservation rather than
                            // a weekly obligation, so an empty week is not a missed post.
                            border: slot.occasional ? '1px dashed' : '1px solid',
                            borderColor: 'divider',
                            borderLeft: 3,
                            borderLeftStyle: slot.occasional ? 'dashed' : 'solid',
                            borderLeftColor: getPlatformColor(platform.id),
                            borderRadius: 1,
                            p: 1,
                            minHeight: 62,
                            cursor: 'default',
                            transition: 'box-shadow 120ms',
                            '&:hover': { boxShadow: 2 },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', fontWeight: 600, lineHeight: 1.3 }}
                          >
                            {slot.theme}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {formatTime(slot.time)}
                            {slot.occasional && ' · as needed'}
                          </Typography>

                          {/* A day can carry two themes — a standing weekly post plus an
                              occasional one. Divided rather than merged, so it is clear these
                              are two posts and not one with an odd name. */}
                          {slot.also && (
                            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 600, lineHeight: 1.3 }}
                              >
                                {slot.also.theme}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                {formatTime(slot.also.time)}
                                {slot.also.occasional && ' · as needed'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Dashed slots are occasional — a standing reservation for when there is something to
            post, not a weekly commitment.
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {scheduled.map(p => {
              const all = slotsFor(p.id);
              const weekly = all.filter(x => !x.slot.occasional).length;
              const occasional = all.length - weekly;
              return (
                <Typography key={p.id} variant="caption" color="text.secondary">
                  <strong>{p.name}:</strong> {weekly}/week
                  {occasional > 0 && ` + ${occasional} as needed`}
                </Typography>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* ---------------- Posting more than once a day ---------------- */}
      {/* Added because the schedule itself puts two themes on Monday, so the question is not
          hypothetical, and because the answer differs enough per network to be worth stating
          rather than leaving to judgement. */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Posting more than once a day</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Each network's own rule is on its card below. These are the parts that apply across
            all of them.
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {SPACING_GUIDANCE.map(item => (
              <Box key={item.title} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ---------------- Per-network detail ---------------- */}
      <Typography variant="h6" sx={{ mb: 0.5 }}>By network</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Best windows, what to avoid, and how each network's format differs.
      </Typography>

      {platforms.map(platform => {
        const guide = GUIDANCE[platform.id];
        if (!guide) return null;
        const comingSoon = platform.status !== 'available';

        return (
          <Accordion
            key={platform.id}
            expanded={expanded === platform.id}
            onChange={(_, isExpanded) => setExpanded(isExpanded ? platform.id : false)}
            sx={{ opacity: comingSoon ? 0.65 : 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: getPlatformColor(platform.id),
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontWeight: 500, flexGrow: 1 }}>
                  {getPlatformName(platform.id)}
                </Typography>
                <Chip
                  size="small"
                  label={guide.headline}
                  variant="outlined"
                  sx={{ fontSize: '0.72rem' }}
                />
                {comingSoon && <Chip size="small" label="Coming soon" />}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {comingSoon && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No integration yet, so nothing can be scheduled here. The notes below are for
                  when it exists.
                </Alert>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Detail icon={<TrendingUpIcon fontSize="small" />} label="Best days" value={guide.bestDays} />
                <Detail icon={<ScheduleIcon fontSize="small" />} label="Best times" value={guide.bestTimes} />
                <Detail icon={<ScheduleIcon fontSize="small" />} label="Also worth trying" value={guide.secondary} />
                <Detail icon={<AvoidIcon fontSize="small" />} label="Avoid" value={guide.avoid} />
              </Box>

              <Detail icon={<CadenceIcon fontSize="small" />} label="Cadence" value={guide.cadence} />

              {guide.sameDay && guide.sameDay !== '—' && (
                <Detail
                  icon={<CadenceIcon fontSize="small" />}
                  label="Posting twice in one day"
                  value={guide.sameDay}
                />
              )}

              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Why these windows
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {guide.why}
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 2, mb: 0.5 }}>
                Format notes
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {guide.format.map((item, i) => (
                  <Box component="li" key={i} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Alert severity="warning" sx={{ mt: 3 }}>
        <AlertTitle>The schedule does not post anything by itself</AlertTitle>
        This page is a reference. To make a slot happen, schedule the post from{' '}
        <Link component="button" onClick={() => navigate('/create')} sx={{ verticalAlign: 'baseline' }}>
          Create Post
        </Link>{' '}
        or from the{' '}
        <Link component="button" onClick={() => navigate('/calendar')} sx={{ verticalAlign: 'baseline' }}>
          Calendar
        </Link>
        .
      </Alert>
    </Box>
  );
};

const Detail: React.FC<{ icon: React.ReactElement; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">{value}</Typography>
    </Box>
  </Box>
);
